import {
  normalizeRouteAuth,
  type RouteAuthMode,
} from "../../shared/monitoringRouteAuth";
import {
  isRouteType,
  isWebpageRoute,
  pickPrimaryRoute,
  type RouteType,
} from "../../shared/stepConfig";
import type { SharedLink } from "../../shared/links";
import type { AppBranch, AppRoute, PageRow } from "./PagesList";

export type PageCardRouteFilter = {
  typeFilter: RouteType | null;
  favFilter: boolean;
  routeFavoriteSet: ReadonlySet<string>;
};

export type PageCardEntry = {
  row: PageRow;
  branch: AppBranch;
  route: AppRoute;
  // All of the workflow's webpage routes when they're collapsed into this one
  // card; the count badge shows the total and lists them.
  webpageRoutes?: AppRoute[];
};

export type LinkListCard = { entry: PageCardEntry } | { link: SharedLink };

// The name the card shows. Name sort orders by this for every link.
export function linkCardStepName(
  card:
    | {
        entry: {
          route: { title: string | null };
          row: { workflow: { name: string } };
        };
      }
    | { link: { title: string | null; route: string } }
): string {
  if ("link" in card) return card.link.title ?? card.link.route;
  return card.entry.route.title ?? card.entry.row.workflow.name;
}

// The single source of truth for which routes render as cards, shared by the
// grid and the header count so they can't disagree (the type pill and the
// Favorites tab both apply here). `routeFavoriteSet` is keyed
// `${workflowId}:${stepDir}`.
export function visiblePageCardRoutes(
  rows: PageRow[],
  { typeFilter, favFilter, routeFavoriteSet }: PageCardRouteFilter
): PageCardEntry[] {
  const out: PageCardEntry[] = [];
  for (const row of rows) {
    const matches: PageCardEntry[] = [];
    for (const branch of row.workflow.branches ?? []) {
      if (branch.name !== "main") continue;
      for (const route of branch.headCommit?.routes ?? []) {
        if (!isRouteType(route.routeType)) continue;
        if (typeFilter && route.routeType !== typeFilter) continue;
        if (
          favFilter &&
          !routeFavoriteSet.has(`${row.workflow.id}:${route.stepDir}`)
        )
          continue;
        matches.push({ row, branch, route });
      }
    }
    const webpageRoutes = matches
      .filter((m) => isWebpageRoute(m.route.routeType))
      .map((m) => m.route);
    const primaryWebpage = pickPrimaryRoute(webpageRoutes);
    for (const m of matches) {
      if (!isWebpageRoute(m.route.routeType)) {
        out.push(m);
      } else if (m.route === primaryWebpage) {
        out.push(webpageRoutes.length > 1 ? { ...m, webpageRoutes } : m);
      }
    }
  }
  return out;
}

export function countPageCards(
  rows: PageRow[],
  filter: PageCardRouteFilter
): number {
  return visiblePageCardRoutes(rows, filter).length;
}

export type CardScope = { mode: RouteAuthMode; mixed: boolean };

// Most-exposed first, so a collapsed card whose links disagree still takes the
// color of its most-exposed link — a public route can't hide behind grey "Mixed".
const SCOPE_BY_EXPOSURE: RouteAuthMode[] = [
  "public",
  "external_id",
  "sso",
  "tenant",
  "connected_app",
  "space",
];

// The access scope a card's badge shows. A single route, or a collapsed card
// whose links share one scope, reports that scope; a collapsed card whose links
// disagree is `mixed`, reported as its most-exposed scope.
export function cardScope(
  route: AppRoute,
  webpageRoutes?: AppRoute[]
): CardScope {
  const routes = webpageRoutes?.length ? webpageRoutes : [route];
  const modes = new Set(routes.map((r) => normalizeRouteAuth(r.routeAuth)));
  if (modes.size === 1) return { mode: [...modes][0]!, mixed: false };
  const mode = SCOPE_BY_EXPOSURE.find((m) => modes.has(m)) ?? "space";
  return { mode, mixed: true };
}
